<template>
	<section class="flex flex-col">
		<Title>Candidate Shortlist</Title>
		<Meta name="description" :content="$t('seo.pages.home.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.home.title')" />
		<Meta property="og:description" :content="$t('seo.pages.home.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />

		<CandidateShortlistModal
			v-model="modalRef"
			@successful-order="onSuccessfulOrder"
			:title="selectedBundle.title"
			:type="selectedBundle.type"
			:logo="selectedBundle.logo"
			:price="selectedBundle.price"
			:newprice="selectedBundle.newprice"
			:region="region"
		/>
		<CandidateShortlistSuccessModal v-model="successRef" />
		<section class="relative flex flex-col items-center text-center pb-dj-70 pt-dj-100">
			<h1 class="text-4xl md:text-7xl max-w-3xl font-semibold">DreamJobs Candidate Shortlist</h1>
			<h3 class="text-2xl md:text-3xl text-dj-red mt-dj-100 mb-dj-80">{{ $t("csl.dreamCandidate") }}</h3>
			<div
				class="grid place-items-center text-2xl rounded-full border border-dj-gray w-dj-60 h-dj-60 cursor-pointer"
				@click="scrollToPrices"
			>
				<BaseIcon icon="ic:outline-arrow-downward" />
			</div>
			<BaseCloudImage
				class="md:hidden absolute left-0 bottom-0"
				custom-class="h-[129px]"
				image-class="h-full"
				public-id="v1653295820/content/DreamJobs/Graphics/bg_hero-mobile-left"
				postfix=".svg"
				alt="buildings on left"
			/>
			<BaseCloudImage
				class="md:hidden absolute right-0 bottom-0"
				custom-class="h-[170px]"
				image-class="h-full"
				public-id="v1653295820/content/DreamJobs/Graphics/bg_hero-mobile-right"
				postfix=".svg"
				alt="buildings on right"
			/>
			<BaseCloudImage
				class="hidden md:block absolute right-0 top-1/2 -translate-y-1/2"
				custom-class="w-auto h-[400px]"
				image-class="h-full"
				public-id="v1651757774/content/DreamJobs/Graphics/pic_candidate_woman_target"
				postfix=".svg"
				alt="woman graphics"
			/>
			<BaseCloudImage
				class="hidden md:block absolute -left-32 top-1/2 -translate-y-1/2"
				custom-class="w-auto h-[380px]"
				image-class="h-full"
				public-id="v1651757774/content/DreamJobs/Graphics/pic_magnifier"
				postfix=".svg"
				alt="man with magnifier"
			/>
		</section>
		<SectionsLogos />
		<section class="py-dj-60 md:py-dj-100 csl-gradient">
			<div class="flex items-center px-5 md:px-0 md:max-w-dj-lg md:mx-auto">
				<div class="flex-1">
					<h2 class="font-semibold text-3xl md:text-5xl md:leading-normal mb-dj-40 md:mb-dj-60">
						{{ $t("csl.whatIsCsl") }}
					</h2>
					<p class="text-lg md:text-xl mb-6" v-html="$t('csl.whatIsParagraph.1')"></p>
					<p class="text-lg md:text-xl mb-dj-40 md:mb-dj-60" v-html="$t('csl.whatIsParagraph.2')"></p>
					<BaseButton @click="scrollToPrices" class="w-full md:w-80 mb-dj-40 md:mb-dj-60" type="primary-red">
						{{ $t("csl.interested") }}
					</BaseButton>
					<span class="text-sm text-dj-gray-dark">{{ $t("csl.whatIsFootnote") }}</span>
				</div>
				<BaseCloudImage
					class="hidden md:block ml-52"
					custom-class="h-auto w-[480px]"
					image-class="w-full"
					public-id="v1651757774/content/DreamJobs/Graphics/pic_candidate_robot_arm"
					postfix=".svg"
					alt="robot arm"
				/>
			</div>
		</section>
		<section>
			<div class="relative md:max-w-[1600px] md:mx-auto md:pb-20 pb-[940px]">
				<BaseCloudImage
					custom-class="h-[280px] md:h-auto"
					image-class="h-[280px] md:h-auto w-auto object-cover"
					public-id="v1651584048/content/DreamJobs/Graphics/bg_candidate-woman"
					postfix=".png"
					:transformation="[{ width: 1600, height: 760, crop: 'scale' }]"
					alt="smiling woman"
				/>
				<div class="absolute top-0 md:top-auto md:bottom-0 left-1/2 -translate-x-1/2 md:w-[1400px]">
					<h3
						class="max-w-[300px] md:max-w-[600px] mb-dj-40 md:mb-[135px] mt-7 md:mt-0 leading-[45px] md:leading-[75px] text-3xl md:text-5xl font-semibold text-white"
					>
						{{ $t("csl.whyUs") }}
					</h3>
					<div
						class="flex flex-col items-center gap-y-3 md:gap-y-0 md:items-start md:flex-row md:justify-between"
					>
						<CandidateShortlistBox v-for="item in 4" :key="item" :content="$t(`csl.services.${item}`)" />
					</div>
				</div>
			</div>
		</section>
		<section class="text-center my-dj-60 md:my-dj-100">
			<h3 class="font-semibold text-3xl w-60 mx-auto md:w-auto md:text-5xl mb-dj-60">
				{{ $t("csl.whichCase") }}
			</h3>
			<div
				class="flex flex-col md:flex-row items-center md:items-start md:justify-between md:max-w-dj-lg mx-auto"
			>
				<div class="flex flex-col justify-center items-center mb-10 md:mb-0">
					<BaseCloudImage
						class="mb-5 md:mb-9 w-[150px] h-[150px] md:w-auto md:h-auto"
						public-id="v1651676587/content/DreamJobs/Graphics/pic_laptop_gears"
						postfix=".png"
						:transformation="[{ width: 300, height: 300, crop: 'scale' }]"
						alt="notebook with gears"
					/>
					<span class="text-2xl font-medium">{{ $t("csl.cases.1") }}</span>
				</div>
				<div class="flex flex-col justify-center items-center mb-10 md:mb-0">
					<BaseCloudImage
						class="mb-5 md:mb-9 w-[150px] h-[150px] md:w-auto md:h-auto"
						public-id="v1651676587/content/DreamJobs/Graphics/pic_man_with_flag"
						postfix=".png"
						:transformation="[{ width: 300, height: 300, crop: 'scale' }]"
						alt="man with flag"
					/>
					<span class="text-2xl font-medium">{{ $t("csl.cases.2") }}</span>
				</div>
				<div class="flex flex-col justify-center items-center">
					<BaseCloudImage
						class="mb-5 md:mb-9 w-[150px] h-[150px] md:w-auto md:h-auto"
						public-id="v1651676587/content/DreamJobs/Graphics/pic_bag_star"
						postfix=".png"
						:transformation="[{ width: 300, height: 300, crop: 'scale' }]"
						alt="a star with a bag in the middle"
					/>
					<span class="text-2xl font-medium">{{ $t("csl.cases.3") }}</span>
				</div>
			</div>
		</section>
		<section>
			<div
				class="bg-dj-blue-light rounded-big max-w-dj-lg mx-auto h-max py-dj-60 px-5 md:pl-[120px] md:pr-[60px]"
			>
				<h2 class="text-3xl md:text-5xl font-semibold mb-dj-40 md:mb-dj-60">{{ $t("csl.howItWorks") }}</h2>
				<div class="flex items-center">
					<div class="w-full md:w-auto">
						<CandidateShortlistList @scroll-to-prices="scrollToPrices" />
						<BaseButton
							@click="scrollToPrices"
							class="ml-0 md:ml-[116px] w-full md:w-80 my-dj-40 md:my-dj-60"
							type="primary-red"
						>
							{{ $t("csl.interested") }}
						</BaseButton>
						<span class="text-sm text-dj-gray-dark ml-0 md:ml-[116px]">
							{{ $t("csl.howItWorksFootnote") }}
						</span>
					</div>
					<BaseCloudImage
						class="hidden md:block ml-16"
						custom-class="h-auto min-w-[320px]"
						image-class="w-full"
						public-id="v1651680418/content/DreamJobs/Graphics/pic_question_marks"
						postfix=".png"
						:transformation="[{ width: 341, height: 788, crop: 'scale' }]"
						alt="question marks"
					/>
				</div>
			</div>
		</section>
		<section ref="cslPricesRef" class="relative h-max pb-[1260px] md:pb-0 md:mt-dj-100 bg-dj-blue-light">
			<BaseCloudImage
				class="mx-auto"
				image-class="h-[280px] md:h-auto object-cover"
				public-id="v1651739223/content/DreamJobs/Graphics/bg_candidate-people"
				postfix=".png"
				:transformation="[{ width: 1600, height: 822, crop: 'scale' }]"
				alt="smiling people"
			/>
			<h3
				class="absolute top-0 left-1/2 -translate-x-1/2 mt-dj-60 md:mt-[60px] text-3xl md:text-5xl text-white font-semibold"
			>
				{{ $t("csl.prices") }}
			</h3>
			<div
				class="absolute flex flex-col gap-y-5 md:gap-y-0 md:flex-row items-center md:items-start md:justify-between md:w-[1280px] left-1/2 md:top-1/2 top-0 -translate-x-1/2 md:-translate-y-1/2 translate-y-40"
			>
				<CandidateShortlistCard
					class="mt-dj-30"
					v-for="(card, index) in cards"
					:key="index"
					:title="$t(`csl.${card.title}`)"
					:type="card.type"
					:listPrice="card.listPrice"
					:actualPrice="card.actualPrice"
					:logo="card.logo"
					@onOrder="selectBundle"
				/>
			</div>
		</section>
		<section
			class="flex justify-center bg-gradient-to-b from-white to-[#f4f5fb] pb-dj-60 md:pb-dj-80 pt-dj-60 md:pt-dj-100"
		>
			<BaseCloudImage
				class="hidden md:block -scale-x-100 mr-[15%]"
				custom-class="h-auto max-w-[380px]"
				image-class="w-full"
				public-id="v1651584047/content/DreamJobs/Graphics/pic_superpower"
				alt="multiple tasks at once graphics"
				postfix=".png"
				:transformation="[{ width: 758, height: 883, crop: 'scale' }]"
			/>
			<div class="flex flex-col px-dj-30 md:px-0">
				<h3
					class="font-semibold text-3xl md:text-5xl md:leading-[75px] mb-dj-40"
					v-html="$t('csl.question')"
				></h3>
				<div class="flex items-center mb-dj-40">
					<div
						class="grid place-content-center bg-dj-red-light rounded-full w-dj-60 md:w-dj-80 h-dj-60 md:h-dj-80"
					>
						<BaseCloudImage
							image-class="h-[24px]"
							public-id="v1651744178/content/DreamJobs/Graphics/icon_candidate-pen"
							postfix=".png"
							alt="pen icon"
						/>
					</div>
					<span class="ml-5 md:ml-dj-30 text-[20px] md:text-3xl">{{ emailAddress }}</span>
				</div>
				<div class="flex items-center">
					<div
						class="grid place-content-center bg-dj-blue-light rounded-full w-dj-60 md:w-dj-80 h-dj-60 md:h-dj-80"
					>
						<BaseCloudImage
							image-class="h-[24px]"
							public-id="v1651744178/content/DreamJobs/Graphics/icon_candidate_message"
							postfix=".png"
							alt="message icon"
						/>
					</div>
					<span class="ml-5 md:ml-dj-30 text-[20px] md:text-3xl">{{ phoneNumber }}</span>
				</div>
			</div>
		</section>
	</section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "CandidateShortlistPage",
	setup() {
		const modalRef = ref(false);
		const successRef = ref(false);

		const { locale } = useI18n();
		const router = useRouter();
		const region = ref("HU");
		const { getEmail } = usePublicConfig();

		const emailAddress = ref(getEmail("hello"));
		const phoneNumber = ref("+36 70 346 4330");

		const gtm = useGTM();

		definePageMeta({
			middleware: "candidate",
		});

		const selectedBundle = ref({
			title: "title",
			type: "type",
			logo: "logo",
			price: "price",
			newprice: "newprice",
		});

		const cards = ref(false);

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;

			emailAddress.value = region.value == "HU" ? getEmail("hello", "HU") : getEmail("business", "RO");
			phoneNumber.value = region.value == "HU" ? "+36 70 346 4330" : "+40 741 123 679";
			cards.value = [
				{
					title: "itCandidates",
					type: "it",
					listPrice: region.value == "HU" ? "599 000 Ft" : "8 899 RON",
					actualPrice: region.value == "HU" ? null : null,
					logo: "v1657110535/content/DreamJobs/Graphics/pic_candidate_it",
				},
				{
					title: "managementPos",
					type: "business",
					listPrice: region.value == "HU" ? "599 000 Ft" : "9 899 RON",
					actualPrice: region.value == "HU" ? null : null,
					logo: "v1657110535/content/DreamJobs/Graphics/pic_candidate_business",
				},
				{
					title: "officeCandidates",
					type: "office",
					listPrice: region.value == "HU" ? "449 000 Ft" : "6 699 RON",
					actualPrice: region.value == "HU" ? null : null,
					logo: "v1657110535/content/DreamJobs/Graphics/pic_candidate_office",
				},
			];
		});

		const cslPricesRef = ref<HTMLElement>();

		function scrollToPrices() {
			window.scrollTo({ top: cslPricesRef.value.offsetTop, left: 0, behavior: "smooth" });
		}

		function selectBundle(evt) {
			modalRef.value = true;
			selectedBundle.value = evt;
		}

		function onSuccessfulOrder() {
			modalRef.value = false;
			successRef.value = true;
			gtm.trackEvent({ event: "csl-sendform-success" });

			router.push(`/${locale.value}/candidate-shortlist#csl-sendform-success`);
		}

		return {
			cards,
			cslPricesRef,
			scrollToPrices,
			selectedBundle,
			selectBundle,
			modalRef,
			successRef,
			onSuccessfulOrder,
			emailAddress,
			phoneNumber,
			region,
		};
	},
});
</script>

<style>
.csl-red-hl {
	@apply text-dj-red font-medium;
}

.csl-white-hl {
	@apply text-white font-medium;
}

.csl-black-hl {
	@apply text-dj-black font-medium;
}

.csl-gradient {
	background: linear-gradient(0deg, #ffffff 0%, #ffffff 0.07%, #f4f5fb 100%);
}
</style>
